import { Component, EventEmitter, HostBinding, HostListener, OnInit, Output, viewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Sheet } from '@app/enums/sheet.enum';
import { Site } from '@app/models/site.model';
import { SiteBookingSettings } from '@app/models/siteBookingSettings.model';
import { ConfigService } from '@app/services/config/config.service';
import { ContentService } from '@app/services/content.service';
import { FeatureFlagService, FeatureKey } from '@app/services/feature-flag.service';
import { CUSTOMER_ID, LocalStorageService, SITE, SITE_BOOKING_SETTINGS } from '@app/services/local-storage.service';
import { UserAccountService } from '@app/services/user-account.service';
import { hexToRgb, sanitizeColor } from '@app/shared/helpers/extensions';
import { BrnSheet } from '@spartan-ng/brain/sheet';

export enum ScreenSize {
    Mobile = 'mobile',
    Tablet = 'tablet',
    Desktop = 'desktop',
}

@Component({
    selector: 'app-login-form',
    templateUrl: './login-form.component.html',
    styleUrls: ['./login-form.component.css'],
    standalone: false,
})
export class LoginFormComponent implements OnInit {
    @HostBinding('class') classes = 'login-component-container';
    /** Emits true for create-account, false for direct login. */
    @Output() closeContainer = new EventEmitter<boolean>();
    @Output() closeSheet = new EventEmitter<Sheet>();
    public url: string = '';
    private site: Site;
    public customerIdWithoutPrefix: string = '';
    public userAccountFeatureEnabled: boolean = true;
    public readonly sheetRef = viewChild<BrnSheet>('sheet');
    private redesignFeatureEnabled: boolean = false;

    /** Set when nested create-account sheet needs to close before emitting closeContainer. */
    private pendingCloseContainer = false;

    constructor(
        private localStorageService: LocalStorageService,
        private userAccountService: UserAccountService,
        public contentService: ContentService,
        private router: Router,
        private featureFlagService: FeatureFlagService,
        private readonly configService: ConfigService
    ) {
        this.site = this.localStorageService.get(SITE) as Site;
    }

    ngOnInit() {
        this.userAccountFeatureEnabled = this.featureFlagService.getFeatureFlag(FeatureKey.UserAccounts);
        const authService = this.configService.config.authService;
        const customerID = this.localStorageService.getString(CUSTOMER_ID);
        this.customerIdWithoutPrefix = customerID?.replace(/.*?(\d+)/, '$1'); //NOSONAR
        const siteBookingSettings = this.localStorageService.get(SITE_BOOKING_SETTINGS) as SiteBookingSettings;
        const themeColor = hexToRgb(siteBookingSettings.themeColor);
        const sanitizedThemeColor = sanitizeColor(themeColor);
        const screenSize = this.getScreenSize();
        this.url = `${authService}?customerID=${this.customerIdWithoutPrefix}&removeExistingTokens=1&color=${sanitizedThemeColor}&viewSize=${screenSize}&application=dsb`;
        this.redesignFeatureEnabled = this.featureFlagService.getFeatureFlag(FeatureKey.Redesign);
    }

    @HostListener('window:message', ['$event'])
    public onMessageReceived(event: any): void {
        if (
            event.data &&
            event.data.data &&
            event.data.result === 'success' &&
            event.data.data.idToken &&
            event.data.data.accessToken &&
            event.data.data.refreshToken
        ) {
            this.userAccountService
                .login(event.data.data.idToken, event.data.data.accessToken, event.data.data.refreshToken, this.customerIdWithoutPrefix)
                .subscribe({
                    next: () => {
                        if (this.redesignFeatureEnabled) {
                            this.closeSheet.emit(Sheet.Main);
                            this.closeSheet.emit(Sheet.Login);
                        }
                        this.closeBottomSheet();
                        this.userAccountService.handleAccountCreation(
                            this.site,
                            this.contentService.selectedStepIndex,
                            this.navigateToRoute.bind(this)
                        );
                    },
                    error: (error) => {
                        console.error('Login error', error);
                    },
                });
        }
    }

    public getScreenSize(): ScreenSize {
        const tabletQueries = [
            'screen and (min-device-width: 768px) and (max-device-width: 1024px) and (orientation: portrait)',
            'screen and (min-width: 1080px) and (max-width: 1080px) and (device-width: 1080px) and (orientation: landscape) and (min-resolution: 1dppx)',
            'screen and (min-width: 1024px) and (max-width: 1024px) and (device-width: 1024px) and (orientation: landscape) and (min-resolution: 1dppx)',
            'screen and (min-width: 1180px) and (max-width: 1180px) and (device-width: 1180px) and (orientation: landscape) and (min-resolution: 1dppx)',
            'screen and (min-width: 1194px) and (max-width: 1194px) and (device-width: 1194px) and (orientation: landscape) and (min-resolution: 1dppx)',
            'screen and (min-width: 1366px) and (max-width: 1366px) and (device-width: 1366px) and (orientation: landscape) and (min-resolution: 1dppx)',
            'screen and (min-width: 1368px) and (max-width: 1368px) and (device-width: 1368px) and (orientation: landscape) and (min-resolution: 1dppx)',
            'screen and (min-device-width: 602px) and (orientation: landscape) and (min-resolution: 1.331dppx) and (max-resolution: 1.332dppx)',
            'screen and (min-device-width: 602px) and (orientation: landscape) and (min-resolution: 2dppx) and (device-aspect-ratio: 40 / 23)',
            'screen and (min-width: 1280px) and (device-width: 1280px) and (max-width: 1280px) and (orientation: landscape) and (min-resolution: 1dppx) and (device-aspect-ratio: 8 / 5)',
        ];
        const desktopQueries = ['screen and (min-width: 1024px)'];

        if (tabletQueries.some((query) => window.matchMedia(query).matches)) {
            return ScreenSize.Tablet;
        } else if (desktopQueries.some((query) => window.matchMedia(query).matches)) {
            return ScreenSize.Desktop;
        } else {
            return ScreenSize.Mobile;
        }
    }

    public closeBottomSheet(isCreateAccount: boolean = false): void {
        this.closeContainer.emit(isCreateAccount);
    }

    public createAccount(): void {
        this.sheetRef()?.open();
    }

    public onCloseContainer(): void {
        this.pendingCloseContainer = true;
        this.sheetRef()?.close();
    }

    public onNestedSheetClosed(): void {
        if (this.pendingCloseContainer) {
            this.pendingCloseContainer = false;
            this.closeBottomSheet(true);
        }
    }

    public forgetPassword() {
        const url = this.site?.bookingURL + '/User/ForgotPassword';
        window.location.href = url.startsWith('http') ? url : 'https://' + url;
    }

    private navigateToRoute(selectedStep: any) {
        void this.router.navigate([selectedStep.stepRoute], {
            queryParams: {
                DSID: this.localStorageService.getString(CUSTOMER_ID),
            },
        });

        this.contentService.isStepCompleted = selectedStep.index;
    }
}
